// form-unified.component.ts
import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  FormBuilder,
  FormGroup,
  FormArray,
  ReactiveFormsModule,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

@Pipe({
  name: 'safeUrl',
  standalone: true,
})
export class SafeUrlPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}
  transform(url: string | null): SafeResourceUrl | null {
    if (!url) return null;
    return this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }
}
import { FormDataService } from '../services/form-data.service';
import { FormStep } from '../services/form-modal.service';
import { PdfGeneratorService } from '../services/pdf-generator.service';
import { ElectronicSignatureService, ElectronicSignatureData } from '../services/electronic-signature.service';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-form-unified',
    imports: [CommonModule, ReactiveFormsModule, FormsModule, TranslateModule, SafeUrlPipe],
    templateUrl: './form-unified.component.html',
    styleUrls: ['./form-unified.component.scss']
})
export class FormUnifiedComponent implements OnInit, OnDestroy {
  @Input() serviceType: string = '';
  @Input() prefilledData: any = null;
  @Output() next = new EventEmitter<any>();
  @Output() previous = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();
  @Output() complete = new EventEmitter<any>();

  currentStep: 'one' | 'two' | 'three' = 'one';
  form!: FormGroup;

  // Propriétés de l'étape 1
  serviceTypes: any[] = [];
  durations: any[] = [];
  diffusionTypes: string[] = [];
  audioFormats: any[] = [];
  hasOtherFormat: boolean = false;
  isRecording: boolean = false;
  recordingTime: string = '00:00';
  minDate: string;

  // Propriétés de l'étape 3 - Signature électronique
  signatureMethod: 'draw' | 'upload' | null = null;
  hasSignature = false;
  isLoggedIn = false;
  private signaturePad: any;
  private isDrawing = false;
  private lastX = 0;
  private lastY = 0;
  // Indique que l'utilisateur a dessiné mais n'a pas encore confirmé via OK
  drawDirty = false;
  
  // Propriétés pour upload de signature
  isSignatureDragOver = false;
  uploadedSignature: string | null = null;
  
  // Méthode "initiales" supprimée selon le besoin

  // Propriétés pour les références améliorées
  isDragOver = false;
  uploadedFile: File | null = null;

  // Propriétés pour le système de rendez-vous
  currentMonth = new Date();
  selectedDate: Date | null = null;
  selectedTimeSlot: string | null = null;
  weekDays = ['WEEKDAYS.MON', 'WEEKDAYS.TUE', 'WEEKDAYS.WED', 'WEEKDAYS.THU', 'WEEKDAYS.FRI', 'WEEKDAYS.SAT', 'WEEKDAYS.SUN'];
  calendarDays: any[] = [];
  availableSlots = [
    '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
    '14:00', '14:30', '15:00', '15:30', '16:00', '16:30', '17:00'
  ];

  private prefillSub?: Subscription;

  // PDF preview state
  quoteObjectUrl: string | null = null;
  // Dedicated URL for iframe viewer (data URL preferred so zoom param is honored)
  quoteViewerUrl: string | null = null;
  showQuoteModal = false;

  constructor(
    private fb: FormBuilder,
    private formDataService: FormDataService,
    private pdfGeneratorService: PdfGeneratorService,
    private electronicSignatureService: ElectronicSignatureService,
    private translateService: TranslateService,
    private sanitizer: DomSanitizer,
  ) {
    const today = new Date();
    this.minDate = today.toISOString().split('T')[0];
  }

  private showErrorToast(message: string) {
    const toast = document.createElement('div');
    toast.className = 'error-toast';
    toast.textContent = message;
    document.body.appendChild(toast);

    setTimeout(() => toast.classList.add('show'), 10);
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 300);
    }, 3000);
  }

  ngOnInit(): void {
    this.initForm();
    this.initData();

    // Charger les données pré-remplies ou sauvegardées
    const dataToLoad = this.prefilledData || this.formDataService.getFormData();
    // Harmoniser la clé "fonction" avec d'éventuels alias (jobTitle, position)
    if (dataToLoad && dataToLoad.personalInfo) {
      const pi = dataToLoad.personalInfo;
      if (!pi.fonction && (pi.jobTitle || pi.position)) {
        pi.fonction = pi.jobTitle || pi.position;
      }
    }
    if (dataToLoad && Object.keys(dataToLoad).length > 0) {
      // Appliquer les valeurs pré-remplies ou sauvegardées au formulaire
      this.form.patchValue(dataToLoad);

  // Les champs personnels restent éditables (pas de verrouillage)

      // Restaurer les services sélectionnés (seulement pour les données sauvegardées, pas pré-remplies)
      if (!this.prefilledData && dataToLoad.selectedServices && dataToLoad.selectedServices.length > 0) {
        this.selectedServices = [...dataToLoad.selectedServices];

        // Utiliser requestAnimationFrame pour améliorer les performances
        requestAnimationFrame(() => {
          this.selectedServices.forEach((service) => {
            const serviceId = service.serviceId;

            // Cocher la case du service
            const checkbox = document.getElementById(
              serviceId
            ) as HTMLInputElement;
            if (checkbox) checkbox.checked = true;

            // Afficher les options du service
            const serviceOptions = document.getElementById(
              `service-options-${serviceId}`
            );
            if (serviceOptions) serviceOptions.classList.remove('hidden');

            // Cocher la durée sélectionnée
            const durationCheckbox = document.getElementById(
              `duration-${serviceId}-${service.duration}`
            ) as HTMLInputElement;
            if (durationCheckbox) durationCheckbox.checked = true;

            // Mettre à jour la quantité
            const quantityInput = document.getElementById(
              `quantity-${serviceId}-${service.duration}`
            ) as HTMLInputElement;
            if (quantityInput)
              quantityInput.value = service.quantity.toString();
          });
        });
      }
      
      // Restaurer les lieux de diffusion sélectionnés (seulement pour les données sauvegardées)
      if (!this.prefilledData && dataToLoad.diffusionTypes && dataToLoad.diffusionTypes.length > 0) {
        const diffusionArray = this.form.get('diffusionTypes') as FormArray;
        // Vider le tableau existant
        while (diffusionArray.length) {
          diffusionArray.removeAt(0);
        }
        
        // Ajouter les valeurs sauvegardées
        dataToLoad.diffusionTypes.forEach((type: string) => {
          diffusionArray.push(this.fb.control(type));
          
          // Cocher les cases correspondantes
          setTimeout(() => {
            const checkbox = document.getElementById(`diffusion-${type}`) as HTMLInputElement;
            if (checkbox) checkbox.checked = true;
          }, 0);
        });
      }
      
      // Restaurer les formats audio sélectionnés (seulement pour les données sauvegardées)
      if (!this.prefilledData && dataToLoad.audioFormats && dataToLoad.audioFormats.length > 0) {
        const formatArray = this.form.get('audioFormats') as FormArray;
        // Vider le tableau existant
        while (formatArray.length) {
          formatArray.removeAt(0);
        }
        
        // Ajouter les valeurs sauvegardées
        dataToLoad.audioFormats.forEach((format: string) => {
          formatArray.push(this.fb.control(format));
          
          // Cocher les cases correspondantes
          setTimeout(() => {
            const checkbox = document.getElementById(`format-${format}`) as HTMLInputElement;
            if (checkbox) checkbox.checked = true;
            
            // Afficher le champ "autre format" si nécessaire
            if (format === 'other') {
              this.hasOtherFormat = true;
            }
          }, 0);
        });
      }
    }

    this.prefillSub = this.formDataService.formData$.subscribe((data: any) => {
      if (data?.personalInfo) {
  this.form.get('personalInfo')?.patchValue(data.personalInfo || {}, { emitEvent: false });
      }
    });

    // Persister dynamiquement les informations personnelles pour éviter les pertes
    const personalGroup = this.form.get('personalInfo') as FormGroup;
    personalGroup.valueChanges.subscribe(() => {
      const pi = (this.form.get('personalInfo') as FormGroup).getRawValue();
      this.formDataService.updateFormData({ personalInfo: pi });
    });
  }

  ngOnDestroy(): void {
    this.prefillSub?.unsubscribe();
  }

  // Removed locking of personal info fields to keep them editable at all times

  // Ajoutez cette méthode pour initialiser les données
  initData(): void {
    // Initialiser les types de services
    this.serviceTypes = [
      {
        id: 'soundtrack',
        label: 'SERVICE_SOUNDTRACK',
        tooltip: 'SERVICE_SOUNDTRACK_TOOLTIP',
      },
      {
        id: 'voiceover',
        label: 'SERVICE_VOICEOVER',
        tooltip: 'SERVICE_VOICEOVER_TOOLTIP',
      },
      {
        id: 'jingle',
        label: 'SERVICE_JINGLE',
        tooltip: 'SERVICE_JINGLE_TOOLTIP',
      },
      {
        id: 'sounddesign',
        label: 'SERVICE_SOUNDDESIGN',
        tooltip: 'SERVICE_SOUNDDESIGN_TOOLTIP',
      },
      {
        id: 'hymne',
        label: 'SERVICE_HYMN',
        tooltip: 'SERVICE_HYMN_TOOLTIP',
      },
      {
        id: 'droits',
        label: 'SERVICE_RIGHTS',
        tooltip: 'SERVICE_RIGHTS_TOOLTIP',
      },
    ];

    // Initialiser les durées
    this.durations = [
      { value: '30sec', label: '30 secondes' },
      { value: '1min', label: '1 minute' },
      { value: '2min', label: '2 minutes' },
      { value: 'custom', label: 'Autre durée' },
    ];

    // Initialiser les types de diffusion
    this.diffusionTypes = ['DIFFUSION_WEB', 'DIFFUSION_TV', 'DIFFUSION_RADIO', 'DIFFUSION_EVENT', 'DIFFUSION_INTERNAL'];

    // Initialiser les formats audio
    this.audioFormats = [
      { value: 'mp3', label: 'FORMAT_MP3' },
      { value: 'wav', label: 'FORMAT_WAV' },
      { value: 'aiff', label: 'FORMAT_AIFF' },
      { value: 'other', label: 'FORMAT_OTHER' },
    ];
  }

  serviceDurations: { [key: string]: any[] } = {
    soundtrack: [
      { value: '30sec', label: '30 secondes', price: 70 },
      { value: '1min', label: '1 minute', price: 85 },
      { value: '2min', label: '2 minutes', price: 105 },
      { value: '5min', label: '5 minutes', price: 130 },
      { value: 'orchestra', label: 'Avec orchestre', price: 170 },
    ],
    jingle: [
      { value: '30sec', label: '30 secondes', price: 160 },
      { value: '2min', label: '2 minutes', price: 250 },
      { value: 'lyrics', label: 'Avec paroles', price: 2000 },
      { value: 'orchestra', label: 'Avec orchestre', price: 3500 },
    ],
    voiceover: [
      { value: '30sec', label: '30 secondes (voix masculine)', price: 50 },
      { value: '2min', label: '2 minutes (voix féminine)', price: 80 },
      {
        value: 'native-male',
        label: 'Langue maternelle (voix masculine)',
        price: 100,
      },
      {
        value: 'native-female',
        label: 'Langue maternelle (voix féminine)',
        price: 120,
      },
      { value: '5min', label: '5 minutes (professionnel)', price: 130 },
    ],
    sounddesign: [
      { value: 'effect', label: 'Effet sonore', price: 9 },
      { value: '30sec', label: 'Ambiance sonore (30 secondes)', price: 70 },
      { value: '2min', label: 'Ambiance sonore (2 minutes)', price: 85 },
      { value: 'pack10', label: 'Pack de 10 effets sonores', price: 105 },
      { value: 'custom', label: 'Pack personnalisé', price: 299 },
    ],
    hymne: [
      { value: '2min', label: 'Format court (2 minutes)', price: 299 },
      { value: '5min', label: 'Format moyen (5 minutes)', price: 499 },
      { value: 'lyrics', label: "Hymne d'entreprise avec paroles", price: 999 },
      {
        value: 'orchestra',
        label: "Hymne d'organisation avec orchestre",
        price: 1499,
      },
    ],
    droits: [
      { value: 'limited', label: 'Licence limitée (1 an)', price: 200 },
      { value: 'extended', label: 'Licence étendue (5 ans)', price: 500 },
      { value: 'unlimited', label: 'Licence illimitée', price: 1000 },
      { value: 'exclusive', label: 'Droits exclusifs', price: 2000 },
    ],
  };

  // Services sélectionnés avec leurs durées et quantités
  selectedServices: {
    serviceId: string;
    serviceName: string;
    duration: string;
    durationLabel: string;
    price: number;
    quantity: number;
  }[] = [];

  // --- Validators helpers ---
  private minSelected(min: number) {
    return (control: AbstractControl): ValidationErrors | null => {
      const arr = control as FormArray;
      const length = arr?.length ?? 0;
      return length >= min ? null : { minSelected: { required: min, actual: length } };
    };
  }

  private appointmentRequired(group: AbstractControl): ValidationErrors | null {
    const g = group as FormGroup;
    const type = g.get('type')?.value;
    const date = g.get('date')?.value;
    const time = g.get('time')?.value;
    if (!type || type === 'none') return { appointmentMissing: true };
    if (!date || !time) return { appointmentIncomplete: true };
    return null;
  }

  initForm(): void {
    this.form = this.fb.group({
      personalInfo: this.fb.group({
        nom: ['', Validators.required],
        prenom: ['', Validators.required],
        email: ['', [Validators.required, Validators.email]],
        telephone: [''],
        entreprise: [''],
        fonction: [''],
      }),
      projectDescription: ['', Validators.required],
      duration: [''],
      customDuration: [''],
      serviceCounts: this.fb.group({}),
      otherFormat: [''],
      rightsType: [''], // optional
      demoOption: ['', Validators.required],
      referenceType: ['', Validators.required],
      referenceLink: [''],
      referenceDescription: [''],
      appointment: this.fb.group(
        {
          type: ['', Validators.required],
          date: [''],
          time: [''],
        },
        { validators: [this.appointmentRequired.bind(this)] }
      ),
      diffusionTypes: this.fb.array([], [this.minSelected(1)]),
      audioFormats: this.fb.array([], [this.minSelected(1)]),
    });
  }

  // Navigation entre les étapes
  goToNextStep() {
    if (this.currentStep === 'one') {
      if (this.form.valid && this.selectedServices.length > 0) {
        // Sauvegarder les données du formulaire et les services sélectionnés
        const formData = {
          ...this.form.getRawValue(),
          selectedServices: this.selectedServices,
          diffusionTypes: (this.form.get('diffusionTypes') as FormArray).value,
          audioFormats: (this.form.get('audioFormats') as FormArray).value
        };
        this.formDataService.updateFormData(formData);

        this.currentStep = 'two';
        this.next.emit({
          step: this.currentStep,
          ...formData,
        });
      } else {
        this.markFormGroupTouched(this.form);

        // Vérifier si aucun service n'est sélectionné
        if (this.selectedServices.length === 0) {
          this.showErrorToast('Veuillez sélectionner au moins un service');
        } else {
          this.showErrorToast('Veuillez remplir tous les champs obligatoires');
        }
      }
    } else if (this.currentStep === 'two') {
      this.currentStep = 'three';
      this.refreshQuotePreview();

      // Récupérer les données actuelles pour les transmettre
  const currentData = this.formDataService.getFormData();
      this.next.emit({
        step: this.currentStep,
        ...currentData,
      });
    }
  }

  goToPreviousStep() {
    if (this.currentStep === 'two') {
      this.currentStep = 'one';
      this.previous.emit({ step: this.currentStep });
    } else if (this.currentStep === 'three') {
      this.currentStep = 'two';
      this.previous.emit({ step: this.currentStep });
    }
  }

  cancelForm() {
    // Réinitialiser sans perdre les infos personnelles
    const piSnapshot = (this.form.get('personalInfo') as FormGroup).getRawValue();
    this.form.reset();
    this.form.get('personalInfo')?.patchValue(piSnapshot);
    this.selectedServices = [];
    this.hasOtherFormat = false;
    this.uploadedFile = null;
    this.isRecording = false;
    this.recordingTime = '00:00';
    this.signatureMethod = null;
    this.clearSignature();
    if (this.quoteObjectUrl) {
      URL.revokeObjectURL(this.quoteObjectUrl);
      this.quoteObjectUrl = null;
    }
    this.currentStep = 'one';
    this.cancel.emit();
  }

  // Legacy HTML summary generator removed in favor of PDF-only display

  onServiceSelect(event: Event, serviceId: string): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    const serviceOptions = document.getElementById(`service-options-${serviceId}`);
    if (serviceOptions) {
      if (isChecked) serviceOptions.classList.remove('hidden');
      else serviceOptions.classList.add('hidden');
    }
    if (!isChecked) {
      // Supprimer ce service des services sélectionnés
      this.selectedServices = this.selectedServices.filter((s) => s.serviceId !== serviceId);
      this.formDataService.updateFormData({ selectedServices: this.selectedServices });
    }
  }

  onServiceDurationChange(event: Event, serviceId: string): void {
    const selectElement = event.target as HTMLSelectElement;
    const durationValue = selectElement.value;

    if (!durationValue) return;

    // Trouver le service dans la liste
    const service = this.serviceTypes.find((s) => s.id === serviceId);
    if (!service) return;

    // Trouver la durée sélectionnée
    const duration = this.serviceDurations[serviceId].find(
      (d) => d.value === durationValue
    );
    if (!duration) return;

    // Vérifier si ce service avec cette durée existe déjà
    const existingIndex = this.selectedServices.findIndex(
      (s) => s.serviceId === serviceId && s.duration === durationValue
    );

    if (existingIndex >= 0) {
      // Mettre à jour la quantité
      const quantityInput = document.getElementById(
        `quantity-${serviceId}-${durationValue}`
      ) as HTMLInputElement;
      if (quantityInput) {
        this.selectedServices[existingIndex].quantity =
          parseInt(quantityInput.value) || 1;
      }
    } else {
      // Ajouter un nouveau service
      this.selectedServices.push({
        serviceId: serviceId,
        serviceName: service.label,
        duration: durationValue,
        durationLabel: duration.label,
        price: duration.price,
        quantity: 1,
      });
    }
    // Mettre à jour le formulaire
    this.formDataService.updateFormData({
      selectedServices: this.selectedServices,
    });
  }

  updateServiceQuantity(
    serviceId: string,
    durationValue: string,
    event: Event
  ): void {
    const target = event.target as HTMLInputElement;
    if (!target) return;

    const quantity = parseInt(target.value) || 1;

    const index = this.selectedServices.findIndex(
      (s) => s.serviceId === serviceId && s.duration === durationValue
    );

    if (index >= 0) {
      this.selectedServices[index].quantity = quantity;
      // Mettre à jour le formulaire
      this.formDataService.updateFormData({
        selectedServices: this.selectedServices,
      });
    }
  }

  onDurationChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    // Logique pour gérer le changement de durée
  }

  onDiffusionSelect(event: Event, diffusionType: string): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    const diffusionArray = this.form.get('diffusionTypes') as FormArray;
    
    if (isChecked) {
      // Ajouter le type de diffusion s'il est coché
      diffusionArray.push(this.fb.control(diffusionType));
    } else {
      // Supprimer le type de diffusion s'il est décoché
      const index = diffusionArray.controls.findIndex(control => control.value === diffusionType);
      if (index >= 0) {
        diffusionArray.removeAt(index);
      }
    }
    
    // Mettre à jour les données du formulaire
    this.formDataService.updateFormData({
      diffusionTypes: diffusionArray.value
    });
  }

  onFormatSelect(event: Event, formatValue: string): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    const formatArray = this.form.get('audioFormats') as FormArray;
    
    if (formatValue === 'other') {
      this.hasOtherFormat = isChecked;
      const otherCtrl = this.form.get('otherFormat');
      if (this.hasOtherFormat) {
        otherCtrl?.setValidators([Validators.required]);
      } else {
        otherCtrl?.clearValidators();
        otherCtrl?.setValue('');
      }
      otherCtrl?.updateValueAndValidity();
    }
    
    if (isChecked) {
      // Ajouter le format s'il est coché
      formatArray.push(this.fb.control(formatValue));
    } else {
      // Supprimer le format s'il est décoché
      const index = formatArray.controls.findIndex(control => control.value === formatValue);
      if (index >= 0) formatArray.removeAt(index);
    }
    
    // Mettre à jour les données du formulaire
    this.formDataService.updateFormData({
      audioFormats: formatArray.value
    });
  }

  // onFileUpload(event: Event): void {
  //   const file = (event.target as HTMLInputElement).files?.[0];
  //   // Logique pour gérer l'upload de fichier
  // }

  toggleRecording(): void {
    this.isRecording = !this.isRecording;
    if (this.isRecording) {
      // Logique pour démarrer l'enregistrement
    } else {
      // Logique pour arrêter l'enregistrement
    }
  }

  // Méthodes de l'étape 3
  // generateDevis removed: legacy HTML summary no longer used (PDF-only)

  // Build/refresh the PDF preview using base template and current data
  async refreshQuotePreview() {
    try {
      const data = this.formDataService.getFormData();
      // Attach selected services in case not persisted yet
      const merged = { ...data, selectedServices: this.selectedServices };
      // Include signature image if already provided
      let signatureDataUrl: string | undefined;
      if (this.signatureMethod === 'draw' && this.hasSignature) {
        const canvas = document.querySelector('#signatureCanvas') as HTMLCanvasElement;
        if (canvas) signatureDataUrl = canvas.toDataURL('image/png');
      } else if (this.signatureMethod === 'upload' && this.uploadedSignature) {
        signatureDataUrl = this.uploadedSignature;
      }

      const blob = await this.pdfGeneratorService.buildFilledQuotePDF(merged, { signatureDataUrl });
      // Cleanup previous object URL
      if (this.quoteObjectUrl) URL.revokeObjectURL(this.quoteObjectUrl);
      this.quoteObjectUrl = this.pdfGeneratorService.toObjectUrl(blob);

      // Set a quick fallback viewer URL using object URL + zoom hint
      this.quoteViewerUrl = this.quoteObjectUrl + '#zoom=page-width';

      // Also try to produce a data URL for better zoom support in some browsers
      const fr = new FileReader();
      fr.onload = () => {
        const dataUrl = fr.result as string;
        // Append zoom for viewer; keep page-width for devis and modal
        this.quoteViewerUrl = dataUrl + '#zoom=page-width';
      };
      fr.onerror = () => {
        // Keep fallback already set
        console.warn('Could not produce data URL for PDF preview; using object URL fallback.');
      };
      fr.readAsDataURL(blob);
    } catch (e) {
      console.error('Error building quote preview', e);
    }
  }

  // Modal controls
  openQuoteModal() {
    if (this.quoteObjectUrl) this.showQuoteModal = true;
  }
  closeQuoteModal() {
    this.showQuoteModal = false;
  }
  onBackdropClick(evt: MouseEvent) {
    // Do not close on backdrop click per requirement (close only via X)
    evt.stopPropagation();
  }

  // Utility: mark all controls as touched to trigger validation messages
  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach(control => {
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.markFormGroupTouched(control as any);
      } else {
        control.markAsTouched();
        control.updateValueAndValidity({ onlySelf: true });
      }
    });
  }

  downloadQuote() {
    if (!this.quoteObjectUrl) return;
    // Fetch blob from object URL and trigger download
    fetch(this.quoteObjectUrl)
      .then(r => r.blob())
      .then(blob => this.pdfGeneratorService.downloadPDF(blob, `devis-applisound-${new Date().toISOString().slice(0,10)}.pdf`));
  }

  // Méthodes de signature électronique
  selectSignatureMethod(method: 'draw' | 'upload') {
    this.signatureMethod = method;
    this.clearSignature();
    
    if (method === 'draw') {
      setTimeout(() => this.initializeCanvas(), 100);
    }
    // Mettre à jour l'aperçu (supprime l'ancienne signature si méthode change)
    this.refreshQuotePreview();
  }
  
  initializeCanvas() {
    const canvas = document.querySelector('#signatureCanvas') as HTMLCanvasElement;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
      }
    }
  }
  
  startDrawing(event: MouseEvent | TouchEvent) {
    this.isDrawing = true;
    const canvas = event.target as HTMLCanvasElement;
    const rect = canvas.getBoundingClientRect();
    
    if (event instanceof MouseEvent) {
      this.lastX = event.clientX - rect.left;
      this.lastY = event.clientY - rect.top;
    } else {
      event.preventDefault();
      this.lastX = event.touches[0].clientX - rect.left;
      this.lastY = event.touches[0].clientY - rect.top;
    }
  }
  
  draw(event: MouseEvent | TouchEvent) {
    if (!this.isDrawing) return;
    
    const canvas = event.target as HTMLCanvasElement;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    let currentX, currentY;
    
    if (event instanceof MouseEvent) {
      currentX = event.clientX - rect.left;
      currentY = event.clientY - rect.top;
    } else {
      event.preventDefault();
      currentX = event.touches[0].clientX - rect.left;
      currentY = event.touches[0].clientY - rect.top;
    }
    
    if (ctx) {
      ctx.beginPath();
      ctx.moveTo(this.lastX, this.lastY);
      ctx.lineTo(currentX, currentY);
      ctx.stroke();
      
      this.lastX = currentX;
      this.lastY = currentY;
      this.drawDirty = true;
    }
  }
  
  stopDrawing() {
    this.isDrawing = false;
    // Attendre la confirmation explicite via OK
  }

  // Confirme la signature dessinée (bouton OK)
  confirmDrawSignature() {
    if (this.signatureMethod === 'draw' && this.drawDirty) {
      this.hasSignature = true;
      this.drawDirty = false;
      this.refreshQuotePreview();
    }
  }
  
  // Upload de signature
  onSignatureDragOver(event: DragEvent) {
    event.preventDefault();
    this.isSignatureDragOver = true;
  }
  
  onSignatureDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isSignatureDragOver = false;
  }
  
  onSignatureDrop(event: DragEvent) {
    event.preventDefault();
    this.isSignatureDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleSignatureFile(files[0]);
    }
  }
  
  onSignatureUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleSignatureFile(input.files[0]);
    }
  }
  
  private handleSignatureFile(file: File) {
    // Vérifier la taille (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      this.showErrorToast('Le fichier est trop volumineux (max 2MB)');
      return;
    }
    
    // Vérifier le type
    if (!file.type.match(/^image\/(png|jpeg)$/)) {
      this.showErrorToast('Format non supporté. Utilisez PNG ou JPEG.');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      this.uploadedSignature = e.target?.result as string;
      this.hasSignature = true;
      // Rafraîchir l'aperçu PDF dès que la signature est importée
      if (this.signatureMethod === 'upload') {
        this.refreshQuotePreview();
      }
    };
    reader.readAsDataURL(file);
  }
  
  removeUploadedSignature(event: Event) {
    event.stopPropagation();
    this.uploadedSignature = null;
    this.hasSignature = false;
    // Retirer la signature de l'aperçu PDF
    if (this.signatureMethod === 'upload') {
      this.refreshQuotePreview();
    }
  }

  clearSignature() {
    this.hasSignature = false;
    this.drawDirty = false;
    
    if (this.signatureMethod === 'draw') {
      const canvas = document.querySelector('#signatureCanvas') as HTMLCanvasElement;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }
      }
    } else if (this.signatureMethod === 'upload') {
      this.uploadedSignature = null;
    }
  }

  // Méthode "initiales" supprimée
  
  hasValidSignature(): boolean {
    switch (this.signatureMethod) {
      case 'draw':
        return this.hasSignature;
      case 'upload':
        return !!this.uploadedSignature;
      default:
        return false;
    }
  }

  validateSignature() {
    if (this.hasValidSignature()) {
      // Récupérer toutes les données du formulaire
      const formData = this.formDataService.getFormData();
      
      // Préparer les données de signature
      let signatureRawData = '';
      
      switch (this.signatureMethod) {
        case 'draw':
          const canvas = document.querySelector('#signatureCanvas') as HTMLCanvasElement;
          if (canvas) {
            signatureRawData = canvas.toDataURL('image/png');
          }
          break;
        case 'upload':
          signatureRawData = this.uploadedSignature || '';
          break;
      }
      
      // Générer les métadonnées de signature conformes eIDAS
      const signatureData: ElectronicSignatureData = 
        this.electronicSignatureService.generateSignatureMetadata(
          this.signatureMethod!,
          signatureRawData
        );
      
      // Valider la signature selon les normes légales
      const validation = this.electronicSignatureService.validateSignature(signatureData);
      
      if (!validation.isValid) {
        this.showErrorToast('Signature non conforme: ' + validation.errors.join(', '));
        return;
      }
      
      // Générer le certificat de signature
      const documentId = 'DOC-' + Date.now();
      const certificate = this.electronicSignatureService.generateSignatureCertificate(
        signatureData, 
        documentId
      );
      
      // Générer le rapport de conformité
      const complianceReport = this.electronicSignatureService.generateComplianceReport(signatureData);
      
      // Ajouter toutes les données légales
      const finalData = {
        ...formData,
        signature: signatureData,
        signatureValidation: validation,
        signatureCertificate: certificate,
        complianceReport: complianceReport,
        submittedAt: new Date().toISOString(),
        legalAcceptance: {
          cgvAccepted: true,
          signatureMethod: this.signatureMethod,
          timestamp: new Date().toISOString(),
          complianceLevel: validation.complianceLevel,
          legalFramework: 'eIDAS + Code civil français'
        },
        documentId
      };
      
      // Générer le PDF avec signature avant de finaliser
      this.generateSignedPDF(finalData).then(() => {
        // Afficher un message de succès
        this.showSuccessMessage(validation.complianceLevel);
        
        // Émettre l'événement de complétion avec toutes les données
        this.complete.emit(finalData);
      }).catch((error) => {
        console.error('Erreur lors de la finalisation:', error);
        this.showErrorToast('Erreur lors de la finalisation de la signature');
      });
    }
  }
  
  private showSuccessMessage(complianceLevel: string) {
    const levelText = {
      'simple': 'Signature électronique simple',
      'advanced': 'Signature électronique avancée', 
      'qualified': 'Signature électronique qualifiée'
    }[complianceLevel] || 'Signature électronique';
    
    // Créer un toast de succès
    const toast = document.createElement('div');
    toast.className = 'success-toast';
    toast.innerHTML = `
      <i class="fas fa-check-circle"></i>
      <div>
        <strong>Signature validée avec succès!</strong><br>
        <small>${levelText} conforme aux normes légales</small>
      </div>
    `;
    document.body.appendChild(toast);
    
    // Animation d'entrée
    setTimeout(() => {
      toast.classList.add('show');
    }, 10);
    
    // Animation de sortie et suppression
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => {
        if (document.body.contains(toast)) {
          document.body.removeChild(toast);
        }
      }, 300);
    }, 4000);
  }
  

  
  private async generateSignedPDF(data: any): Promise<void> {
    try {
      // Valider la signature électronique
      if (!this.pdfGeneratorService.validateElectronicSignature(data.signature)) {
        throw new Error('Signature électronique non valide');
      }
      
      // Générer le PDF avec signature
      const pdfBlob = await this.pdfGeneratorService.generateSignedQuotePDF(data, data.signature);
      
      // Télécharger automatiquement le PDF
      const filename = `devis-applisound-${new Date().toISOString().split('T')[0]}.pdf`;
      this.pdfGeneratorService.downloadPDF(pdfBlob, filename);
      
      console.log('PDF généré et téléchargé avec succès');
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      this.showErrorToast('Erreur lors de la génération du PDF');
    }
  }

  saveDraftAndReturn() {
    // Sauvegarder l'état actuel
  this.formDataService.updateFormData(this.form.getRawValue());
    this.cancel.emit();
  }

  createAccount() {
    // Logique de création de compte
  }

  // Nouvelles méthodes pour les références améliorées
  selectReferenceType(type: string) {
    this.form.patchValue({ referenceType: type });
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFileSelection(files[0]);
    }
  }

  onFileUpload(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFileSelection(input.files[0]);
    }
  }

  private handleFileSelection(file: File) {
    // Vérifier la taille (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      this.showErrorToast('Le fichier est trop volumineux (max 10MB)');
      return;
    }

    // Vérifier le type
    const allowedTypes = ['audio/mpeg', 'audio/wav', 'audio/aiff', 'audio/m4a', 'audio/mp3'];
    if (!allowedTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|aiff|m4a)$/i)) {
      this.showErrorToast('Format de fichier non supporté');
      return;
    }

    this.uploadedFile = file;
  }

  removeUploadedFile() {
    this.uploadedFile = null;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  addToDescription(key: string) {
    const translatedText = this.translateService.instant(`FORM_UNIFIED.${key.toUpperCase()}`);
    const currentValue = this.form.get('referenceDescription')?.value || '';
    const newValue = currentValue ? currentValue + ', ' + translatedText : translatedText;
    this.form.patchValue({ referenceDescription: newValue });
  }

  // Méthodes pour le système de rendez-vous
  selectMeetingType(type: string) {
    this.form.patchValue({ appointment: { ...this.form.get('appointment')?.value, type } });
    this.generateCalendar();
    this.form.get('appointment')?.updateValueAndValidity();
  }

  generateCalendar() {
    const year = this.currentMonth.getFullYear();
    const month = this.currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - (firstDay.getDay() + 6) % 7);

    this.calendarDays = [];
    const currentDate = new Date(startDate);

    for (let i = 0; i < 42; i++) {
      this.calendarDays.push({
        date: new Date(currentDate),
        isCurrentMonth: currentDate.getMonth() === month
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }
  }

  previousMonth() {
    this.currentMonth.setMonth(this.currentMonth.getMonth() - 1);
    this.currentMonth = new Date(this.currentMonth);
    this.generateCalendar();
  }

  nextMonth() {
    this.currentMonth.setMonth(this.currentMonth.getMonth() + 1);
    this.currentMonth = new Date(this.currentMonth);
    this.generateCalendar();
  }

  getAvailableTimeSlots(date: Date): string[] {
    // Placeholder for future API logic; for now, return all slots
    return this.availableSlots;
  }

  isDateSelected(date: Date): boolean {
    return this.selectedDate ? 
      date.toDateString() === this.selectedDate.toDateString() : false;
  }

  isDateAvailable(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date >= today && date.getDay() !== 0 && date.getDay() !== 6; // Pas le weekend
  }

  isToday(date: Date): boolean {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  selectDate(date: Date | null) {
    if (date && this.isDateAvailable(date)) {
      this.selectedDate = date;
      this.selectedTimeSlot = null;
      this.form.patchValue({ appointment: { ...this.form.get('appointment')?.value, date: date.toISOString().split('T')[0] } });
      this.form.get('appointment')?.updateValueAndValidity();
    }
  }

  selectTimeSlot(slot: string) {
    this.selectedTimeSlot = slot;
    this.form.patchValue({ appointment: { ...this.form.get('appointment')?.value, time: slot } });
    this.form.get('appointment')?.updateValueAndValidity();
  }

  getTimeZone(): string {
    return 'CET (UTC+1)';
  }

  getMeetingTypeIcon(): string {
    const type = this.form.get('appointment.type')?.value;
    switch (type) {
      case 'visio': return 'fas fa-video';
      case 'phone': return 'fas fa-phone';
      case 'physical': return 'fas fa-handshake';
      case 'none': return 'fas fa-times-circle';
      default: return 'fas fa-calendar';
    }
  }

  getMeetingTypeLabel(): string {
    const type = this.form.get('appointment.type')?.value;
    switch (type) {
      case 'visio': return 'Visioconférence';
      case 'phone': return 'Appel téléphonique';
      case 'physical': return 'Rencontre physique';
      case 'none': return 'Pas d\'échange souhaité';
      default: return '';
    }
  }

  getMeetingDuration(): string {
    const type = this.form.get('appointment.type')?.value;
    switch (type) {
      case 'visio': return '30 minutes';
      case 'phone': return '20 minutes';
      case 'physical': return '45 minutes';
      case 'none': return 'Contact ultérieur si besoin';
      default: return '';
    }
  }

  isFormValid(): boolean {
    return this.form.valid && this.selectedServices.length > 0;
  }

  // When reading data to submit, use getRawValue() to include disabled fields
  private buildPayload(): any {
    return this.form.getRawValue();
  }
}
